import { useCallback, useEffect, useState } from 'react'

import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'

import {
  Coverages,
  InsuredObjectsOutput,
  PackObjects,
} from '../../@types/RunPetisSimulation'
import { useCoveragesStore } from '../../Stores/useCoveragesStore'
import { t } from '../../i18n'
import { TablePacks } from '../TablePacks'
import {
  _InsuredObjectsType,
  useFormattedValueToTableRender,
} from './useFormattedValueToTableRender'

type Props = {
  data: InsuredObjectsOutput
  index: number
  sequence: number
}

type PackTableType = {
  [key: string]: {
    isPreselected?: boolean
    pack: PackObjects
  }
}

export type RowsTableType = {
  coverageId?: string
  coverageName?: string
  mandatory?: boolean
  order?: number
  coverage?: Coverages
} & PackTableType

export const InsuredObjects = ({ data: _data, index, sequence }: Props) => {
  const [rowsTable, setRowsTable] = useState<RowsTableType[]>([])
  const [packsPrice, setPacksPrice] = useState<PackObjects[]>()
  const data = useFormattedValueToTableRender(_data)
  const { setCoverages } = useCoveragesStore()

  const columnHelper = createColumnHelper<any>()

  const columns = () => {
    if (rowsTable.length > 0) {
      const packListIds = data?.[0]?.packLists?.map(
        (item) => item.packId,
      ) as string[]

      const uniqueColumnsKeys = Array.from(
        new Set(
          rowsTable
            .filter((item) =>
              packListIds?.some((key) => item.hasOwnProperty(key)),
            )
            .flatMap((item) => Object.keys(item)),
        ),
      )

      return uniqueColumnsKeys?.map((key) => {
        return columnHelper.accessor(key, {
          id: key,
          cell: () => {
            return ''
          },
        })
      })
    }
    return []
  }

  const table = useReactTable({
    data: rowsTable || [],
    columns: columns(),
    getCoreRowModel: getCoreRowModel(),
  })

  const tableColumnsHidden = useCallback(
    (columns: string[]) => {
      if (table.getAllColumns().length) {
        for (const column of columns) {
          if (table?.getColumn(column)) {
            table?.getColumn(column)?.toggleVisibility(false)
          }
        }
      }
    },
    [table],
  )

  useEffect(() => {
    tableColumnsHidden(['coverageId', 'coverage', 'mandatory', 'order'])
  }, [table, rowsTable, tableColumnsHidden])

  const tableRowFormatter = (
    dataFormatted: _InsuredObjectsType,
  ): RowsTableType[] => {
    setPacksPrice(dataFormatted.packLists)

    const newRows: RowsTableType[] = []
    dataFormatted?.packLists?.forEach((pack) => {
      dataFormatted?.coverages?.mandatoryCoverages?.forEach(
        (coverageMandatory) => {
          const isPreselected = pack?.coverages?.find(
            (item) =>
              item?.coverageId === coverageMandatory?.coverageId &&
              item?.isPreSelected,
          )

          const itemMandatory = pack?.coverages?.find(
            (i) =>
              i?.coverageId === coverageMandatory?.coverageId &&
              Boolean(i?.mandatory),
          )

          if (Object.keys(isPreselected || {})?.length > 0) {
            const index = newRows?.findIndex((row) => {
              return row?.coverageId === isPreselected?.coverageId
            })

            if (index !== -1) {
              newRows[index] = {
                ...newRows[index],
                mandatory: true,
                order: itemMandatory?.order || 0,
                [pack.packId as string]: {
                  isPreselected: isPreselected?.isPreSelected,
                  pack,
                },
              } as RowsTableType
            } else {
              newRows.push({
                coverageId: coverageMandatory?.coverageId,
                coverageName: coverageMandatory?.coverageName,
                mandatory: true,
                order: itemMandatory?.order || 0,
                [pack.packId as string]: {
                  isPreselected: isPreselected?.isPreSelected,
                  pack,
                },
                coverage: coverageMandatory,
              } as RowsTableType)
            }
          }
        },
      )

      dataFormatted?.coverages?.optionalCoverages?.forEach(
        (coverageOptional) => {
          const index = newRows?.findIndex((row) => {
            return row?.coverageId === coverageOptional?.coverageId
          })

          const itemOptional = pack?.coverages?.find(
            (i) =>
              i?.coverageId === coverageOptional?.coverageId && !i?.mandatory,
          )

          if (index !== -1) {
            newRows[index] = {
              ...newRows[index],
              order: itemOptional?.order || 0,
              mandatory: false,
              [pack.packId as string]: {
                isOptional: true,
                pack,
              },
            } as RowsTableType
          } else {
            newRows.push({
              coverageId: coverageOptional?.coverageId,
              coverageName: coverageOptional?.coverageName,
              mandatory: false,
              order: itemOptional?.order || 0,
              [pack.packId as string]: {
                isOptional: true,
                pack,
              },
              coverage: coverageOptional,
            } as RowsTableType)
          }
        },
      )
    })

    const itemsMandatorySorted = newRows
      ?.filter((item) => item?.mandatory)
      ?.sort((a, b) => a?.order! - b?.order!)

    const itemsOptionalSorted = newRows
      ?.filter((item) => !item?.mandatory)
      ?.sort((a, b) => a?.order! - b?.order!)

    const itemsTable = [
      {
        coverageId: 'default',
        coverageName: t('pages.step2.table.baseCoverageAlt'),
      },
      ...itemsMandatorySorted,
      {
        coverageId: 'optional',
        coverageName: t('pages.step2.table.optionalCoverageAlt'),
      },
      ...itemsOptionalSorted,
    ] as RowsTableType[]

    return itemsTable
  }

  useEffect(() => {
    if ((data || [])?.length > 0) {
      setRowsTable(tableRowFormatter(data?.[0] as _InsuredObjectsType))
      setCoverages({
        [`animal${_data?.insuredObjectId as string}`]: tableRowFormatter(
          data?.[0] as _InsuredObjectsType,
        )?.map(
          (row) =>
            ({
              coverageId: row?.coverageId,
              coverageName: row?.coverageName,
              coverageDescription: row?.coverage?.description,
              asfBranchCode: row?.coverage?.asfBranchCode,
              order: row?.order,
            } as never), // TODO: Check this as never
        ),
      })
    }
  }, [data, setCoverages])

  return (
    <>
      {rowsTable && rowsTable?.length > 0 && (
        <TablePacks
          index={index}
          sequence={sequence}
          animal={{
            ..._data?.insuredPet,
            animalId: _data?.insuredObjectId as string,
            insuredObjectCode: _data.insuredObjectCode as string,
          }}
          packsList={packsPrice as PackObjects[]}
          tablePacks={table}
          tablePacksLength={rowsTable.length}
        />
      )}
    </>
  )
}
